import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, ChevronDown, ChevronUp, Shield, Plus } from "lucide-react";
import UploadExcelButton from "@/components/UploadExcelButton";
import PlayerCreateDialog from "@/components/PlayerCreateDialog";
import { Button } from "@/components/ui/button";
import { FbmStatTile, FbmStatCellGraded } from "@/components/FbmStat";
import DistributionChart from "@/components/DistributionChart";
import TeamComparison from "@/components/TeamComparison";
import { toast } from "sonner";

const TEAM_FBM_DESCRIPTIONS = {
  overall:   "The probability that this team contributes to the overall play of the league.",
  finishing: "The probability that this team is good at finishing.",
  passing:   "The probability that this team is good at passing.",
  defending: "The probability that this team is good at defending.",
};

export default function TeamDetailPage({ basePath = "/admin", canImport = true }) {
  const { id } = useParams();
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const isAdmin = user?.role === "admin";
  const [team, setTeam] = useState(null);
  const [fbm, setFbm] = useState(null);
  const [roster, setRoster] = useState([]);           // [{ id, full_name }, ...]
  const [rosterFbm, setRosterFbm] = useState({});      // { [playerId]: { overall, finishing, passing, defending, fbm_score } | null }
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  // Roster sort state — Phase 11.7 default: FBM desc (best player at top).
  const [sortBy, setSortBy] = useState("fbm");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const [t, f, r] = await Promise.all([
        api.get(`/teams/${id}`),
        api.get(`/teams/${id}/fbm`).catch(() => ({ data: null })),
        api.get(`/teams/${id}/roster-fbm`),
      ]);
      setTeam(t.data);
      setFbm(f.data);
      const rosterPayload = r.data?.players || [];
      // Adapter — flatten into the same row shape the sorted table expects.
      setRoster(rosterPayload.map((row) => ({
        id: row.player_id,
        full_name: row.full_name,
      })));
      const fbmDict = {};
      for (const row of rosterPayload) {
        fbmDict[row.player_id] = {
          overall: row.overall,
          finishing: row.finishing,
          passing: row.passing,
          defending: row.defending,
          fbm_score: row.fbm_score,
          minutes_played: row.minutes_played,
          match_count: row.match_count,
          period_label: row.period_label,
          period_kind: row.period_kind,
        };
      }
      setRosterFbm(fbmDict);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Refetch when team id changes OR when the user toggles ignore_friendlies
  useEffect(() => { load(); }, [id, ignoreFriendlies]);

  // Sorted roster — nulls always last regardless of direction.
  const sortedRoster = useMemo(() => {
    const rows = roster.map((p) => {
      const f = rosterFbm[p.id] || null;
      return {
        id: p.id,
        full_name: p.full_name,
        fbm_score: f?.fbm_score ?? null,
        overall: f?.overall ?? null,
        finishing: f?.finishing ?? null,
        passing: f?.passing ?? null,
        defending: f?.defending ?? null,
        period_label: f?.period_label ?? null,
        period_kind: f?.period_kind ?? null,
        match_count: f?.match_count ?? 0,
      };
    });

    const getVal = (r) => sortBy === "full_name" ? (r.full_name || "") : r[sortBy];

    rows.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      const aNull = av == null || av === "";
      const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;   // nulls always last
      if (bNull) return -1;
      if (sortBy === "full_name") {
        return sortDir === "asc"
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      return sortDir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
    return rows;
  }, [roster, rosterFbm, sortBy, sortDir]);

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      // Sensible default direction per column: name asc, numbers desc.
      setSortDir(key === "full_name" ? "asc" : "desc");
    }
  };

  if (loading && !team) {
    return <div data-testid="team-detail-loading" className="text-muted-foreground">Loading…</div>;
  }
  if (!team) {
    return <div data-testid="team-detail-empty" className="text-muted-foreground">Team not found.</div>;
  }

  return (
    <div data-testid="team-detail-page" className="space-y-8">
      <Link
        to={`${basePath}/teams`}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
        data-testid="team-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to teams
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2 inline-flex items-center gap-2">
            <Shield className="h-3.5 w-3.5" /> Team
          </div>
          <h1
            className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight"
            data-testid="team-detail-name"
          >
            {team.name}
          </h1>
        </div>
        {canImport && (
          <UploadExcelButton
            url={`/teams/${team.id}/import-excel`}
            testId={`upload-team-${team.id}`}
            onResult={load}
            label="Upload Excel"
          />
        )}
      </header>

      {/* FBM probability tiles */}
      {fbm && (
        <section
          className="grid grid-cols-2 md:grid-cols-5 gap-3"
          data-testid="team-fbm-tiles"
        >
          <FbmStatTile
            label="Overall"
            metric="overall"
            value={fbm.overall}
            description={TEAM_FBM_DESCRIPTIONS.overall}
            testId="team-fbm-overall"
          />
          <FbmStatTile
            label="Finishing"
            metric="finishing"
            value={fbm.finishing}
            description={TEAM_FBM_DESCRIPTIONS.finishing}
            testId="team-fbm-finishing"
          />
          <FbmStatTile
            label="Passing"
            metric="passing"
            value={fbm.passing}
            description={TEAM_FBM_DESCRIPTIONS.passing}
            testId="team-fbm-passing"
          />
          <FbmStatTile
            label="Defending"
            metric="defending"
            value={fbm.defending}
            description={TEAM_FBM_DESCRIPTIONS.defending}
            testId="team-fbm-defending"
          />
          <div
            className="rounded-xl border border-border bg-card px-4 py-3 flex flex-col justify-center"
            data-testid="team-fbm-score-tile"
            title="Sum of the four probabilities (Overall + Finishing + Passing + Defending)."
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">FBM score</div>
            <div className="font-mono text-2xl mt-1 tabular-nums" data-testid="team-fbm-score">
              {fbm.fbm_score == null ? "—" : Math.ceil(Number(fbm.fbm_score))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              over {fbm.match_count ?? 0} match{fbm.match_count === 1 ? "" : "es"}
            </div>
          </div>
        </section>
      )}

      {/* Team info panel */}
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="font-display uppercase tracking-tight text-xl">Team info</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-sm" data-testid="team-info-panel">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Name</dt>
              <dd className="mt-1">{team.name}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Country</dt>
              <dd className="mt-1">{team.country || "—"}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Position in table</dt>
              <dd className="mt-1 font-mono" data-testid="team-info-position">
                {team.position_in_table == null ? "—" : team.position_in_table}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Leagues</dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {(team.leagues || []).length === 0
                  ? "—"
                  : (team.leagues || []).map((lg) => (
                      <Link
                        key={lg.id}
                        to={`${basePath}/leagues/${lg.id}`}
                        className="inline-flex items-center rounded border border-border bg-muted/30 px-1.5 py-0.5 text-[11px] hover:bg-muted/60"
                        data-testid={`team-info-league-${lg.id}`}
                      >
                        {lg.name}
                      </Link>
                    ))}
              </dd>
            </div>
            <div className="col-span-2 md:col-span-4 md:col-start-1">
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Created at</dt>
              <dd className="mt-1 font-mono text-xs text-muted-foreground">
                {team.created_at ? String(team.created_at).slice(0, 10) : "—"}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Distribution chart */}
      <DistributionChart teamId={team.id} />

      {/* Team vs Team comparison */}
      <TeamComparison teamId={team.id} currentTeamName={team.name} />

      {/* Roster (Phase 11.7 / 11.8 — adaptive current-form FBM) */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="font-display uppercase tracking-tight text-xl">
              Roster{" "}
              <span className="text-sm text-muted-foreground font-normal" data-testid="team-roster-count">
                ({sortedRoster.length} player{sortedRoster.length === 1 ? "" : "s"})
              </span>
            </CardTitle>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="team-roster-subtitle">
              Current form · isolation on newest season (≥10 matches) or newest + previous
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 shrink-0" data-testid="team-roster-admin-actions">
              <Button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="h-11 rounded-none font-display uppercase tracking-[0.18em]"
                data-testid="team-roster-new-player-button"
              >
                <Plus className="h-4 w-4 mr-2" /> New Player
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {sortedRoster.length === 0 ? (
            <div className="text-muted-foreground text-sm" data-testid="team-roster-empty">
              No players on this team yet.
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table data-testid="team-roster-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <RosterTh label="Name"      sortKey="full_name" sortBy={sortBy} sortDir={sortDir} onClick={onSort} align="left"  testId="roster-sort-name" />
                    <RosterTh label="FBM"       sortKey="fbm_score" sortBy={sortBy} sortDir={sortDir} onClick={onSort}               testId="roster-sort-fbm" />
                    <RosterTh label="Overall"   sortKey="overall"   sortBy={sortBy} sortDir={sortDir} onClick={onSort}               testId="roster-sort-overall" />
                    <RosterTh label="Finishing" sortKey="finishing" sortBy={sortBy} sortDir={sortDir} onClick={onSort}               testId="roster-sort-finishing" />
                    <RosterTh label="Passing"   sortKey="passing"   sortBy={sortBy} sortDir={sortDir} onClick={onSort}               testId="roster-sort-passing" />
                    <RosterTh label="Defending" sortKey="defending" sortBy={sortBy} sortDir={sortDir} onClick={onSort}               testId="roster-sort-defending" />
                    {isAdmin && (
                      <TableHead className="px-4 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right w-[60px]" data-testid="roster-actions-th">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRoster.map((p) => (
                    <TableRow
                      key={p.id}
                      className="border-border hover:bg-muted/30"
                      data-testid={`team-roster-row-${p.id}`}
                    >
                      <TableCell className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`${basePath}/players/${p.id}`}
                            className="font-medium hover:underline"
                            data-testid={`team-roster-name-${p.id}`}
                          >
                            {p.full_name}
                          </Link>
                          {p.period_label && (
                            <span
                              data-testid={`team-roster-period-${p.id}`}
                              className={`text-[10px] uppercase tracking-[0.15em] rounded px-1.5 py-0.5 border ${
                                p.period_kind === "season+prev"
                                  ? "border-amber-500/40 text-amber-500/80"
                                  : "border-border text-muted-foreground"
                              }`}
                              title={
                                p.period_kind === "season+prev"
                                  ? `Newest season has only ${p.match_count} match${p.match_count === 1 ? "" : "es"} in the window — folded in previous season for stability.`
                                  : `Isolation on ${p.period_label} (${p.match_count} matches).`
                              }
                            >
                              {p.period_label.replace(/(\d{2})(\d{2})\/(\d{2})/g, "$2/$3")}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-3 text-right font-mono text-foreground/85" data-testid={`team-roster-fbm-${p.id}`}>
                        {p.fbm_score == null ? <span className="text-muted-foreground">—</span> : (Math.ceil(Number(p.fbm_score) * 10) / 10).toFixed(1)}
                      </TableCell>
                      <TableCell className="px-6 py-3 text-right">
                        <FbmStatCellGraded value={p.overall} testId={`team-roster-overall-${p.id}`} />
                      </TableCell>
                      <TableCell className="px-6 py-3 text-right">
                        <FbmStatCellGraded value={p.finishing} testId={`team-roster-finishing-${p.id}`} />
                      </TableCell>
                      <TableCell className="px-6 py-3 text-right">
                        <FbmStatCellGraded value={p.passing} testId={`team-roster-passing-${p.id}`} />
                      </TableCell>
                      <TableCell className="px-6 py-3 text-right">
                        <FbmStatCellGraded value={p.defending} testId={`team-roster-defending-${p.id}`} />
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="px-4 py-3 text-right whitespace-nowrap" data-testid={`team-roster-actions-${p.id}`}>
                          <UploadExcelButton
                            url={`/players/${p.id}/import-excel`}
                            testId={`upload-player-${p.id}`}
                            onResult={load}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <PlayerCreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          lockedTeamId={team.id}
          lockedTeamName={team.name}
          onCreated={load}
        />
      )}
    </div>
  );
}

/** Sortable header cell (Phase 11.7 — local roster). */
function RosterTh({ label, sortKey, sortBy, sortDir, onClick, align = "right", testId }) {
  const active = sortBy === sortKey;
  return (
    <TableHead
      className={`px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onClick(sortKey)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        {active ? (
          sortDir === "asc"
            ? <ChevronUp className="h-3.5 w-3.5" data-testid={`${testId}-asc`} />
            : <ChevronDown className="h-3.5 w-3.5" data-testid={`${testId}-desc`} />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 opacity-25" />
        )}
      </span>
    </TableHead>
  );
}
